import { FC, useEffect } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { blue } from 'theme/colors';
import { Box, CircularProgress, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { Alert, Button, Input, Snackbar, StrongModalText } from '@atoms';
import { useURLparams } from '@hooks';
import { BoxContainer, FullWidthContent, MainMenuLayout } from '@layouts';
import { zodResolver } from '@hookform/resolvers/zod';
import { FIELDS, initialValues } from './constants';
import { useGetLimitsBySegment } from './data/useGetLimitDetail';
import usePatchLimitDetail from './data/usePatchLimitDetail';
import limitFormSchema, { limitFormSchemaType } from './LimitForm.schema';

/**
 * View and edit Limit screen
 */
const LimitDetailScreen: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { params, setURLparams } = useURLparams<{ tab: string }>();

  const isEditing = params?.tab == 'editar';
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm<limitFormSchemaType>({
    resolver: zodResolver(limitFormSchema),
    mode: 'all',
    defaultValues: initialValues,
  });
  const { data, isLoading: isLoadingLimitData } = useGetLimitsBySegment({ limitId: id });
  useEffect(() => {
    data &&
      reset({
        ...data,
        dailyLimitReceived: data.dailyLimitReceived?.toString(),
        dailyLimitSent: data.dailyLimitSent?.toString(),
        dailyLimitTxReceived: data.dailyLimitTxReceived?.toString(),
        dailyLimitTxSent: data.dailyLimitTxSent?.toString(),
        monthlyLimitReceived: data.monthlyLimitReceived?.toString(),
        monthlyLimitSent: data.monthlyLimitSent?.toString(),
        monthlyLimitTxReceived: data.monthlyLimitTxReceived?.toString(),
        monthlyLimitTxSent: data.monthlyLimitTxSent?.toString(),
        transactionLimitReceived: data.transactionLimitReceived?.toString(),
        transactionLimitSent: data.transactionLimitSent?.toString(),
      });
  }, [data, reset]);

  const {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    isModalOpen,
    setIsModalOpen,
    submit,
  } = usePatchLimitDetail(reset, id);
  const disableButton = !isValid || Object.keys(dirtyFields).length == 0;
  return (
    <MainMenuLayout>
      <FullWidthContent>
        <BoxContainer>
          <Box justifyContent={'space-between'} display={'flex'} alignItems={'center'}>
            <Typography variant='h4'>
              {t(isEditing ? 'LIMITS:EDIT_DETAIL' : 'LIMITS:DETAIL:TITLE')}
            </Typography>
            <Button
              variant='contained'
              onClick={() => {
                reset();
                setURLparams(isEditing ? {} : { tab: 'editar' });
              }}
              text={isEditing ? 'LIMITS:VIEW_DETAIL' : 'COMMON:EDIT'}
            />
          </Box>

          <Box>
            <Grid container spacing={3}>
              {isLoadingLimitData ? (
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                FIELDS.map(({ name, label, type, editable }) => {
                  return (
                    <Grid key={name} item xs={6} md={6}>
                      {editable ? (
                        <EditingField
                          {...{
                            name,
                            label,
                            type,
                            control,
                            isEditing,
                            error: errors[name as keyof limitFormSchemaType]?.message,
                          }}
                        />
                      ) : (
                        <Input
                          label={t(label)}
                          variant='outlined'
                          size='small'
                          fullWidth
                          type={type}
                          value={data && data[name]}
                          disabled
                        />
                      )}
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Box>
          <Alert
            modalVisible={isError}
            handleClose={() => {
              setIsError(false);
            }}
            variant='error'
            title={alertMessage.title}
            description={alertMessage.description}
          />
          <Snackbar
            open={isSuccess}
            message={t(alertMessage.title)}
            severity='success'
            handleClose={() => setIsSuccess(false)}
          />
          <Alert
            variant='warning'
            modalVisible={isModalOpen}
            handleClose={() => setIsModalOpen(false)}
            handleSubmit={handleSubmit((body) => submit(body, dirtyFields))}
            closeButtonText='COMMON:CANCEL'
            title={t('PROSPECTS:SEND_EMAIL:MODAL:TITLE')}
            submitLoading={isLoading}
            content={
              <Typography>
                <Trans
                  i18nKey={'LIMITS:MODAL:CONFIRM_DESCRIPTION'}
                  components={{ s: <StrongModalText /> }}
                />
              </Typography>
            }
          />
          {isEditing && (
            <Box display={'flex'} justifyContent={'flex-end'}>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                isLoading={isLoading}
                disabled={disableButton}
                onClick={() => setIsModalOpen(true)}
                text={t('COMMON:EDIT')}
              />
            </Box>
          )}
        </BoxContainer>
      </FullWidthContent>
    </MainMenuLayout>
  );
};
interface EditingFieldInterface {
  name: string;
  label: string;
  type: string;
  control: Control<limitFormSchemaType>;
  error?: string;
  isEditing: boolean;
}
/**
 * Textbox and checkbox inputs
 */
const EditingField = ({ name, label, type, error, control, isEditing }: EditingFieldInterface) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name as keyof limitFormSchemaType}
      control={control}
      render={({ field }) => {
        if (type === 'boolean') {
          return (
            <Box>
              <Typography variant='subtitle2' color={blue.main}>
                {t(label)}
              </Typography>
              <FormControlLabel
                sx={{ paddingLeft: 2 }}
                label={field.value ? t('COMMON:YES') : t('COMMON:NO')}
                control={
                  <Switch
                    sx={{ marginRight: 1 }}
                    {...field}
                    checked={Boolean(field.value)}
                    disabled={!isEditing}
                  />
                }
              />
            </Box>
          );
        }
        return (
          <Input
            {...field}
            label={t(label)}
            variant='outlined'
            size='small'
            fullWidth
            type={type}
            error={Boolean(error)}
            helperText={error}
            disabled={!isEditing}
          />
        );
      }}
    />
  );
};
export default LimitDetailScreen;
